/**
 * UsuariosAdminPage — gestão de usuários do tenant pelo admin (fase Admin
 * comum).
 *
 * Réplica do admin.html: lista os usuários da prefeitura com papel e
 * permissões, cria, edita (inclusive senha) e exclui — sempre dentro do
 * próprio tenant. Super admin opera aqui sobre a prefeitura escolhida.
 */

import {
  faEdit,
  faExclamationTriangle,
  faPlus,
  faShieldAlt,
  faSpinner,
  faTrashAlt,
  faUser,
  faUsersCog,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  configuracaoAdmin,
  criarUsuario,
  editarUsuario,
  excluirUsuario,
  usuariosDoTenant,
  type ConfiguracaoAdmin,
  type UsuarioSistema,
} from '../../api/admin'
import { useToast } from '../../components/Toast'

const CLASSE_INPUT =
  'w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-slate-600 dark:bg-dark-bg dark:text-slate-100'

interface FormUsuario {
  nome: string
  email: string
  cpf: string
  organizacao: string
  endereco: string
  papel: 'user' | 'admin'
  senha: string
  permissoes: string[]
}

const FORM_VAZIO: FormUsuario = {
  nome: '', email: '', cpf: '', organizacao: '', endereco: '',
  papel: 'user', senha: '', permissoes: [],
}

function ModalUsuario({
  usuario, configuracao, aoFechar,
}: {
  usuario: UsuarioSistema | null // null = criação
  configuracao: ConfiguracaoAdmin
  aoFechar: () => void
}) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const [form, setForm] = useState<FormUsuario>(
    usuario
      ? {
          nome: usuario.nome ?? '',
          email: usuario.email ?? '',
          cpf: usuario.cpf ?? '',
          organizacao: usuario.organizacao ?? '',
          endereco: '',
          papel: usuario.papel === 'admin' ? 'admin' : 'user',
          senha: '',
          permissoes: usuario.permissoes ?? [],
        }
      : FORM_VAZIO,
  )

  const salvar = useMutation({
    mutationFn: () =>
      usuario
        ? editarUsuario(usuario.id, form)
        : criarUsuario(form),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['admin-usuarios'] })
      notificar('success', usuario
        ? 'Usuário atualizado com sucesso!'
        : 'Usuário criado com sucesso!')
      aoFechar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  const alternarPermissao = (chave: string) =>
    setForm((atual) => ({
      ...atual,
      permissoes: atual.permissoes.includes(chave)
        ? atual.permissoes.filter((p) => p !== chave)
        : [...atual.permissoes, chave],
    }))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm"
      role="dialog" aria-modal="true"
      aria-label={usuario ? 'Editar usuário' : 'Novo usuário'}
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
    >
      <form
        className="my-8 w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-dark-card"
        onSubmit={(e) => {
          e.preventDefault()
          salvar.mutate()
        }}
      >
        <h3 className="mb-4 text-xl font-bold text-slate-800 dark:text-slate-100">
          <FontAwesomeIcon icon={usuario ? faEdit : faPlus} className="mr-2 text-indigo-600" />
          {usuario ? `Editar ${usuario.nome || usuario.email}` : 'Novo usuário'}
        </h3>

        <div className="space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Nome completo *</span>
            <input type="text" required value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })} className={CLASSE_INPUT} />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">E-mail *</span>
            <input type="email" required value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} className={CLASSE_INPUT} />
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">CPF</span>
              <input type="text" value={form.cpf}
                onChange={(e) => setForm({ ...form, cpf: e.target.value })} className={CLASSE_INPUT} />
            </label>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Órgão/Setor</span>
              <input type="text" value={form.organizacao}
                onChange={(e) => setForm({ ...form, organizacao: e.target.value })} className={CLASSE_INPUT} />
            </label>
          </div>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
              {usuario ? 'Nova senha (em branco mantém a atual)' : 'Senha *'}
            </span>
            <input type="password" required={!usuario} minLength={usuario ? undefined : 6}
              value={form.senha} autoComplete="new-password"
              onChange={(e) => setForm({ ...form, senha: e.target.value })} className={CLASSE_INPUT} />
          </label>
          <label className="flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
            <input type="checkbox" checked={form.papel === 'admin'}
              onChange={(e) => setForm({ ...form, papel: e.target.checked ? 'admin' : 'user' })}
              className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
            <FontAwesomeIcon icon={faShieldAlt} className="text-indigo-500" />
            Administrador da prefeitura
          </label>

          <fieldset>
            <legend className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              Permissões de módulo
            </legend>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {configuracao.permissoes.map((p) => (
                <label key={p.chave}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:text-slate-300">
                  <input type="checkbox" checked={form.permissoes.includes(p.chave)}
                    onChange={() => alternarPermissao(p.chave)}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                  {p.rotulo}
                </label>
              ))}
            </div>
          </fieldset>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button type="button" onClick={aoFechar}
            className="rounded-xl bg-slate-200 px-5 py-2.5 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300">
            Cancelar
          </button>
          <button type="submit" disabled={salvar.isPending}
            className="rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-2.5 font-semibold text-white shadow-lg transition-all hover:from-indigo-700 hover:to-purple-700 disabled:opacity-60">
            <FontAwesomeIcon icon={salvar.isPending ? faSpinner : faPlus} spin={salvar.isPending} className="mr-2" />
            {usuario ? 'Salvar' : 'Criar usuário'}
          </button>
        </div>
      </form>
    </div>
  )
}

function ModalExcluir({
  usuario, aoFechar,
}: {
  usuario: UsuarioSistema
  aoFechar: () => void
}) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()

  const excluir = useMutation({
    mutationFn: () => excluirUsuario(usuario.id),
    onSuccess: () => {
      void clienteQuery.invalidateQueries({ queryKey: ['admin-usuarios'] })
      notificar('success', 'Usuário excluído.')
      aoFechar()
    },
    onError: (e: Error) => notificar('error', e.message),
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      role="dialog" aria-modal="true" aria-label="Excluir usuário"
      onMouseDown={(e) => { if (e.target === e.currentTarget) aoFechar() }}
    >
      <div className="mx-4 w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-2xl dark:border-slate-700 dark:bg-dark-card">
        <span className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-red-500 to-orange-600 shadow-lg shadow-red-500/30">
          <FontAwesomeIcon icon={faTrashAlt} className="text-2xl text-white" />
        </span>
        <h3 className="mb-2 text-xl font-bold text-slate-800 dark:text-slate-100">Excluir usuário?</h3>
        <p className="mb-4 rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-800 dark:bg-slate-700 dark:text-slate-200">
          {usuario.nome || usuario.email}
        </p>
        <p className="mb-6 text-sm font-semibold text-red-600 dark:text-red-400">
          <FontAwesomeIcon icon={faExclamationTriangle} className="mr-1" />
          O acesso e as permissões deste usuário serão removidos.
        </p>
        <div className="flex justify-center gap-3">
          <button type="button" onClick={aoFechar}
            className="rounded-xl bg-slate-200 px-6 py-3 font-medium text-slate-700 transition-all hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-300">
            Cancelar
          </button>
          <button type="button" onClick={() => excluir.mutate()} disabled={excluir.isPending}
            className="rounded-xl bg-gradient-to-r from-red-500 to-orange-600 px-6 py-3 font-semibold text-white shadow-lg shadow-red-500/30 transition-all hover:from-red-600 hover:to-orange-700 disabled:opacity-60">
            <FontAwesomeIcon icon={excluir.isPending ? faSpinner : faTrashAlt} spin={excluir.isPending} className="mr-2" />
            Sim, Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

export function UsuariosAdminPage() {
  const [criando, setCriando] = useState(false)
  const [editando, setEditando] = useState<UsuarioSistema | null>(null)
  const [excluindo, setExcluindo] = useState<UsuarioSistema | null>(null)

  const { data: usuarios, isLoading, error } = useQuery({
    queryKey: ['admin-usuarios'],
    queryFn: usuariosDoTenant,
    retry: false,
  })
  const { data: configuracao } = useQuery({
    queryKey: ['admin-configuracao'],
    queryFn: configuracaoAdmin,
    staleTime: Infinity,
    retry: false,
  })

  if (isLoading) {
    return (
      <p className="mt-12 text-center text-slate-500">
        <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
        Carregando usuários…
      </p>
    )
  }
  if (error || !usuarios) {
    return (
      <div className="mx-auto mt-12 max-w-xl rounded-2xl bg-white p-8 text-center shadow-lg dark:bg-dark-card">
        <FontAwesomeIcon icon={faUsersCog} className="mb-4 text-5xl text-slate-300" />
        <h1 className="mb-2 text-2xl font-semibold text-slate-700 dark:text-slate-200">
          Não foi possível carregar os usuários
        </h1>
        <p className="text-slate-500">{(error as Error)?.message}</p>
      </div>
    )
  }

  const rotuloPermissao = new Map(
    (configuracao?.permissoes ?? []).map((p) => [p.chave, p.rotulo]),
  )

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100">
            <FontAwesomeIcon icon={faUsersCog} className="mr-3 text-indigo-600" />
            Usuários da Prefeitura
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            {usuarios.length} usuário(s)
            {usuarios[0]?.prefeitura && ` · ${usuarios[0].prefeitura}`}
          </p>
        </div>
        <button type="button" onClick={() => setCriando(true)}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 font-semibold text-white shadow-lg transition-all hover:from-indigo-700 hover:to-purple-700">
          <FontAwesomeIcon icon={faPlus} />
          Novo usuário
        </button>
      </header>

      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-lg dark:border-slate-700 dark:bg-dark-card">
        <table className="w-full text-left text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
            <tr>
              <th className="px-6 py-4">Usuário</th>
              <th className="px-6 py-4">Papel</th>
              <th className="px-6 py-4">Permissões</th>
              <th className="px-6 py-4">Criado em</th>
              <th className="px-6 py-4 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
            {usuarios.map((usuario) => (
              <tr key={usuario.id} className="transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="px-6 py-4">
                  <div className="font-semibold text-slate-800 dark:text-slate-100">
                    {usuario.nome || '—'}
                  </div>
                  <div className="text-xs text-slate-500">{usuario.email}</div>
                </td>
                <td className="px-6 py-4">
                  {usuario.papel === 'super_admin' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2.5 py-1 text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      <FontAwesomeIcon icon={faShieldAlt} /> Super Admin
                    </span>
                  ) : usuario.papel === 'admin' ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                      <FontAwesomeIcon icon={faShieldAlt} /> Admin
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      <FontAwesomeIcon icon={faUser} /> Usuário
                    </span>
                  )}
                </td>
                <td className="px-6 py-4">
                  <div className="flex max-w-xs flex-wrap gap-1">
                    {usuario.permissoes?.length ? usuario.permissoes.map((p) => (
                      <span key={p} className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                        {rotuloPermissao.get(p) ?? p}
                      </span>
                    )) : <span className="text-xs text-slate-400">Nenhuma</span>}
                  </div>
                </td>
                <td className="px-6 py-4 text-slate-500">{usuario.criado_em || '—'}</td>
                <td className="px-6 py-4">
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setEditando(usuario)}
                      aria-label={`Editar ${usuario.nome || usuario.email}`}
                      className="rounded-lg p-2 text-blue-600 transition-colors hover:bg-blue-50 dark:hover:bg-blue-900/20">
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    {usuario.papel !== 'super_admin' && (
                      <button type="button" onClick={() => setExcluindo(usuario)}
                        aria-label={`Excluir ${usuario.nome || usuario.email}`}
                        className="rounded-lg p-2 text-red-600 transition-colors hover:bg-red-50 dark:hover:bg-red-900/20">
                        <FontAwesomeIcon icon={faTrashAlt} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {criando && configuracao && (
        <ModalUsuario usuario={null} configuracao={configuracao}
          aoFechar={() => setCriando(false)} />
      )}
      {editando && configuracao && (
        <ModalUsuario usuario={editando} configuracao={configuracao}
          aoFechar={() => setEditando(null)} />
      )}
      {excluindo && (
        <ModalExcluir usuario={excluindo} aoFechar={() => setExcluindo(null)} />
      )}
    </div>
  )
}
